import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Users, Plus, Edit, Trash2, BarChart3, Briefcase } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const AdminPanel = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [intStats, setIntStats] = useState(null);
  const [internships, setInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: ''
  });

  useEffect(() => {
    if (user?.email === 'admin@eternalcourses.com') {
      fetchUsers();
      fetchStats();
      fetchInternships();
      fetchIntStats();
    }
  }, [user]);

  const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('token')}` });

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/users`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) setUsers(data.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/admin/stats`, { headers: authHeaders() });
      const data = await response.json();
      if (data.success) setStats(data.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchInternships = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/internships`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setInternships(data.data);
    } catch {}
  };

  const fetchIntStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/admin/internship-stats`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setIntStats(data.data);
    } catch {}
  };

  const toggleInternshipActive = async (id, currentActive) => {
    try {
      await fetch(`${API_BASE}/admin/internships/${id}`, {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: currentActive ? 0 : 1 })
      });
      fetchInternships();
    } catch {}
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API_BASE}/admin/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newUser)
      });
      const data = await response.json();
      if (data.success) {
        setShowCreateForm(false);
        setNewUser({ email: '', password: '', first_name: '', last_name: '', phone: '' });
        fetchUsers();
        alert('User created successfully!');
      } else {
        alert(data.message);
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Error creating user');
    }
  };

  const handleInputChange = (e) => {
    setNewUser({
      ...newUser,
      [e.target.name]: e.target.value
    });
  };

  // Check if user is admin
  if (user?.email !== 'admin@eternalcourses.com') {
    return (
      <div className="pt-32 text-center text-earth-500">
        <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
        <p>You need admin privileges to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="pt-32 text-center text-earth-500">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-saffron-600 mx-auto"></div>
        <p className="mt-4">Loading admin panel...</p>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-16 px-4 max-w-7xl mx-auto bg-background min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-4xl font-serif font-bold text-earth-900">Admin Panel</h1>
          {activeTab === 'users' && (
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="flex items-center space-x-2 px-4 py-2 bg-saffron-600 text-white rounded-lg hover:bg-saffron-700 transition-colors"
            >
              <Plus className="w-5 h-5" /><span>Add User</span>
            </button>
          )}
        </div>

        {/* Tab switcher */}
        <div className="flex gap-2 mb-8 border-b border-earth-200">
          {[['users', Users, 'Users'], ['internships', Briefcase, 'Internships']].map(([tab, Icon, label]) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${
                activeTab === tab ? 'border-saffron-500 text-saffron-600' : 'border-transparent text-earth-500 hover:text-earth-700'
              }`}
            >
              <Icon className="w-4 h-4" />{label}
            </button>
          ))}
        </div>

        {activeTab === 'users' && stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card border border-earth-200 rounded-lg p-6">
              <div className="flex items-center">
                <Users className="w-8 h-8 text-saffron-600" />
                <div className="ml-4">
                  <p className="text-sm text-earth-600">Total Users</p>
                  <p className="text-2xl font-bold text-earth-900">{stats.total_users}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-earth-200 rounded-lg p-6">
              <div className="flex items-center">
                <BarChart3 className="w-8 h-8 text-saffron-600" />
                <div className="ml-4">
                  <p className="text-sm text-earth-600">Total Courses</p>
                  <p className="text-2xl font-bold text-earth-900">{stats.total_courses}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-earth-200 rounded-lg p-6">
              <div className="flex items-center">
                <BarChart3 className="w-8 h-8 text-saffron-600" />
                <div className="ml-4">
                  <p className="text-sm text-earth-600">Total Transactions</p>
                  <p className="text-2xl font-bold text-earth-900">{stats.total_transactions}</p>
                </div>
              </div>
            </div>
            <div className="bg-card border border-earth-200 rounded-lg p-6">
              <div className="flex items-center">
                <BarChart3 className="w-8 h-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm text-earth-600">Successful Payments</p>
                  <p className="text-2xl font-bold text-earth-900">{stats.successful_transactions}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create User Form */}
        {activeTab === 'users' && showCreateForm && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-card border border-earth-200 rounded-lg p-6 mb-8"
          >
            <h2 className="text-xl font-bold mb-4">Create New User</h2>
            <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={newUser.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-earth-300 rounded-lg focus:ring-2 focus:ring-saffron-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-2">Password</label>
                <input
                  type="password"
                  name="password"
                  value={newUser.password}
                  onChange={handleInputChange}
                  required
                  minLength={6}
                  className="w-full px-4 py-2 border border-earth-300 rounded-lg focus:ring-2 focus:ring-saffron-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-2">First Name</label>
                <input
                  type="text"
                  name="first_name"
                  value={newUser.first_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-earth-300 rounded-lg focus:ring-2 focus:ring-saffron-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-2">Last Name</label>
                <input
                  type="text"
                  name="last_name"
                  value={newUser.last_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-2 border border-earth-300 rounded-lg focus:ring-2 focus:ring-saffron-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-earth-700 mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={newUser.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border border-earth-300 rounded-lg focus:ring-2 focus:ring-saffron-500 focus:border-transparent"
                />
              </div>
              <div className="flex items-end space-x-4">
                <button
                  type="submit"
                  className="px-6 py-2 bg-saffron-600 text-white rounded-lg hover:bg-saffron-700 transition-colors"
                >
                  Create User
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-6 py-2 border border-earth-300 text-earth-700 rounded-lg hover:bg-earth-50 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </motion.div>
        )}

        {/* ── Internships Tab ─────────────────────────────────────────── */}
        {activeTab === 'internships' && (
          <>
            {/* Internship Stats */}
            {intStats && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  ['Projects', intStats.total_projects],
                  ['Enrollments', intStats.total_enrollments],
                  ['Completions', intStats.total_completions],
                  ['Certificates', intStats.total_certificates],
                ].map(([label, val]) => (
                  <div key={label} className="bg-card border border-earth-200 rounded-lg p-5 flex items-center gap-3">
                    <Briefcase className="w-7 h-7 text-saffron-600 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-earth-500">{label}</p>
                      <p className="text-2xl font-bold text-earth-900">{val}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Projects table */}
            <div className="bg-card border border-earth-200 rounded-lg overflow-hidden">
              <div className="px-6 py-4 border-b border-earth-200">
                <h2 className="text-xl font-bold text-earth-900">Internship Projects ({internships.length})</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-earth-50">
                    <tr>
                      {['Title', 'Category', 'Fee', 'Difficulty', 'Enrolled', 'Completed', 'Status', 'Action'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-earth-500 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-earth-100">
                    {internships.map(p => (
                      <tr key={p.id} className="hover:bg-earth-50">
                        <td className="px-4 py-3 font-medium text-earth-900 max-w-xs truncate">{p.title}</td>
                        <td className="px-4 py-3 text-earth-600">{p.category}</td>
                        <td className="px-4 py-3">₹{Number(p.enrollment_fee).toLocaleString('en-IN')}</td>
                        <td className="px-4 py-3 capitalize">{p.difficulty_level}</td>
                        <td className="px-4 py-3">{p.enrolled_count}</td>
                        <td className="px-4 py-3">{p.completed_count}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${p.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'}`}>
                            {p.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleInternshipActive(p.id, p.is_active)}
                            className="text-xs text-saffron-600 hover:underline"
                          >
                            {p.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* Users List */}
        {activeTab === 'users' && (
        <div className="bg-card border border-earth-200 rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-earth-200">
            <h2 className="text-xl font-bold text-earth-900">All Users</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-earth-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-earth-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-earth-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-earth-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-saffron-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-saffron-600">
                              {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-earth-900">
                            {user.first_name} {user.last_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-earth-900">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-earth-900">{user.phone || '-'}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-earth-900">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button className="text-saffron-600 hover:text-saffron-900 mr-3">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-red-600 hover:text-red-900">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </motion.div>
    </div>
  );
};

export default AdminPanel; 